import React, { createContext, useContext, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { preloadRoute } from "../utils/preloadAssets";

const TRANSITION = {
  duration: 1.2,
  ease: [0.33, 1, 0.68, 1],
};

const transitionByPath = (path) => {
  if (path === "/" || path === "") {
    return {
      bg: "bg-darkBrown",
      in: { initial: { y: "100%" }, animate: { y: 0 } },
      out: { initial: { y: 0 }, animate: { y: "100%" } },
    };
  }
  if (path === "/about") {
    return {
      bg: "bg-darkBrown",
      in: { initial: { y: "-100%" }, animate: { y: 0 } },
      out: { initial: { y: 0 }, animate: { y: "-100%" } },
    };
  }
  if (path === "/contact") {
    return {
      bg: "bg-pink",
      in: { initial: { x: "100%" }, animate: { x: 0 } },
      out: { initial: { x: 0 }, animate: { x: "100%" } },
    };
  }
  if (path.startsWith("/work")) {
    return {
      bg: "bg-lightBrown",
      in: { initial: { x: "-100%" }, animate: { x: 0 } },
      out: { initial: { x: 0 }, animate: { x: "-100%" } },
    };
  }
  return {
    bg: "bg-lightBrown",
    in: { initial: { x: "-100%" }, animate: { x: 0 } },
    out: { initial: { x: 0 }, animate: { x: "-100%" } },
  };
};

const PageTransitionContext = createContext(null);

export function PageTransitionProvider({ children }) {
  const navigate = useNavigate();
  const transitioningRef = useRef(false);
  const [state, setState] = useState({
    isTransitioning: false,
    nextPath: null,
    phase: null,
    config: null,
  });

  const navigateWithTransition = useCallback((path) => {
    const normalized = path === "" ? "/" : path;
    if (transitioningRef.current) return;
    transitioningRef.current = true;

    // 方案A：点击时立即开始预加载目标页资源，与窗帘动画并行执行
    preloadRoute(normalized);

    const config = transitionByPath(normalized);
    setState({
      isTransitioning: true,
      nextPath: normalized,
      phase: "in",
      config,
    });
  }, []);

  const handleInComplete = useCallback(() => {
    setState((prev) => {
      navigate(prev.nextPath, { state: { fromTransition: true } });
      return { ...prev, phase: "out" };
    });
  }, [navigate]);

  const handleOutComplete = useCallback(() => {
    transitioningRef.current = false;
    setState({
      isTransitioning: false,
      nextPath: null,
      phase: null,
      config: null,
    });
  }, []);

  return (
    <PageTransitionContext.Provider value={{ navigateWithTransition }}>
      {children}
      {state.isTransitioning && state.config && (
        <motion.div
          key={state.phase}
          className={`fixed inset-0 z-[100] pointer-events-auto ${state.config.bg}`}
          initial={state.phase === "in" ? state.config.in.initial : state.config.out.initial}
          animate={state.phase === "in" ? state.config.in.animate : state.config.out.animate}
          transition={TRANSITION}
          onAnimationComplete={state.phase === "in" ? handleInComplete : handleOutComplete}
          aria-hidden="true"
          style={{ willChange: "transform" }}
        />
      )}
    </PageTransitionContext.Provider>
  );
}

export function usePageTransition() {
  const ctx = useContext(PageTransitionContext);
  if (!ctx) return { navigateWithTransition: null };
  return ctx;
}
